/**
 * Contrato de notificações do serviço central `imobgo-notifications` para o
 * backoffice (ADR-0025/0028). O servidor NÃO devolve título/corpo prontos: cada
 * notificação carrega só `type` + `payload` cru e o front renderiza o texto por
 * tipo (via i18n). Estes três tipos são os que o `imobgo-backoffice-api` emite
 * para o usuário admin — devem casar 1:1 com o backend.
 */

export interface AdminRoleChangedPayload {
  role: string;
  changedAt: string;
}

export interface AdminTwoFactorResetPayload {
  resetAt: string;
}

export interface AdminAccountStatusChangedPayload {
  status: string;
  changedAt: string;
}

/** Novo chamado aberto por um corretor (origem: crm). `ticketId` roteia o clique. */
export interface SupportTicketOpenedPayload {
  ticketId: string;
  code: string;
  subject: string;
  category: string;
  priority: string;
  requesterName: string;
}

/** Resposta do corretor a um chamado existente. `authorRole` = customer. */
export interface SupportTicketMessageAddedPayload {
  ticketId: string;
  code: string;
  subject: string;
  authorRole: string;
  messageId: string;
}

export type NotificationEvent =
  | { type: "AdminRoleChanged"; payload: AdminRoleChangedPayload }
  | { type: "AdminTwoFactorReset"; payload: AdminTwoFactorResetPayload }
  | { type: "AdminAccountStatusChanged"; payload: AdminAccountStatusChangedPayload }
  | { type: "SupportTicketOpened"; payload: SupportTicketOpenedPayload }
  | { type: "SupportTicketMessageAdded"; payload: SupportTicketMessageAddedPayload };

export type NotificationType = NotificationEvent["type"];

export const NOTIFICATION_TYPES: NotificationType[] = [
  "AdminRoleChanged",
  "AdminTwoFactorReset",
  "AdminAccountStatusChanged",
  "SupportTicketOpened",
  "SupportTicketMessageAdded",
];

export type ConnectionStatus = "connected" | "reconnecting" | "disconnected";

/**
 * Sinal SLIM entregue pelo evento SSE `notification` do serviço central
 * (contrato "option C", ADR-0025): apenas uma dica, NÃO o `NotificationDto`
 * completo. `id` é o UUID/cursor do servidor, `type` o `NotificationType` e
 * `unread` a contagem atual de não lidas. O conteúdo da lista continua vindo do
 * REST (`GET /notifications`); o sinal só dispara reconciliação + toast genérico.
 */
export interface NotificationSignal {
  id: string;
  type: NotificationType;
  unread: number;
}

/** Variante por tipo: garante que `n.type === 'X'` restringe `n.payload`. */
type StoredVariant<T extends NotificationType> = {
  id: string;
  type: T;
  payload: Extract<NotificationEvent, { type: T }>["payload"];
  receivedAt: string;
  read: boolean;
};

export type StoredNotification = {
  [K in NotificationType]: StoredVariant<K>;
}[NotificationType];

/**
 * Notificação persistida vinda do servidor — entregue pela lista REST
 * (`GET /v1/notifications`). O `id` é o do servidor; o front faz upsert por ele
 * (dedup entre REST e o sinal SSE).
 */
export type NotificationDto = {
  [K in NotificationType]: {
    id: string;
    type: K;
    payload: Extract<NotificationEvent, { type: K }>["payload"];
    read: boolean;
    readAt: string | null;
    createdAt: string;
  };
}[NotificationType];

/** Página da lista de notificações + total de não lidas (badge do sino). */
export interface NotificationPage {
  items: NotificationDto[];
  nextCursor: string | null;
  hasMore: boolean;
  unreadCount: number;
}
